/* =====================================================================
   MEG — Experience Engine  v4.0
   3D crystal hero · glowing cursor · scroll progress · page intro ·
   reveals · word-rise · card tilt/shimmer · magnetic buttons · aurora
   Progressive: any layer can fail without breaking the page.
   ===================================================================== */
(function () {
  'use strict';

  var doc = document;
  var root = doc.documentElement;
  root.classList.remove('no-js');

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var wide = window.matchMedia('(min-width: 901px)').matches;
  var hoverable = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var lowPower = (navigator.deviceMemory && navigator.deviceMemory <= 2);

  // Crystal runs on any wide screen with motion allowed + WebGL — NOT gated on pointer.
  var allowCrystal = wide && !reduceMotion && !lowPower && hasWebGL();
  // Cursor / tilt / magnetic need a real pointer.
  var allowPointerFx = hoverable && !reduceMotion;

  function hasWebGL() {
    try {
      var c = doc.createElement('canvas');
      return !!(window.WebGLRenderingContext &&
        (c.getContext('webgl2') || c.getContext('webgl') || c.getContext('experimental-webgl')));
    } catch (e) { return false; }
  }

  var _cache = {};
  function loadScript(src) {
    if (_cache[src]) return _cache[src];
    _cache[src] = new Promise(function (res, rej) {
      var s = doc.createElement('script');
      s.src = src; s.async = true; s.crossOrigin = 'anonymous';
      s.onload = res; s.onerror = rej;
      doc.head.appendChild(s);
    });
    return _cache[src];
  }
  function onIdle(fn) {
    if ('requestIdleCallback' in window) requestIdleCallback(fn, { timeout: 1500 });
    else setTimeout(fn, 400);
  }
  function lerp(a, b, n) { return a + (b - a) * n; }

  /* ===================== PAGE TRANSITIONS (fade) ===================== */
  function initPageTransition() {
    if (reduceMotion) return;
    var ov = doc.createElement('div');
    ov.className = 'meg-page-fade';
    doc.body.appendChild(ov);
    // intro handles fade-in; here we fade OUT to dark on internal navigation
    doc.addEventListener('click', function (e) {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      var a = e.target.closest && e.target.closest('a[href]');
      if (!a) return;
      var href = a.getAttribute('href');
      if (!href || href.charAt(0) === '#' || href.indexOf('mailto:') === 0 ||
          href.indexOf('tel:') === 0 || a.target === '_blank' || a.hasAttribute('download')) return;
      if (a.hostname && a.hostname !== window.location.hostname) return;
      e.preventDefault();
      ov.classList.add('is-on');
      setTimeout(function () { window.location.href = href; }, 420);
    });
    // safety: if a page is restored from bfcache, clear the overlay
    window.addEventListener('pageshow', function () { ov.classList.remove('is-on'); });
  }

  /* ============================ PAGE INTRO ============================ */
  function initIntro() {
    if (reduceMotion) return;
    var intro = doc.createElement('div');
    intro.className = 'meg-intro';
    intro.innerHTML = '<div class="meg-intro__mark"></div>';
    doc.body.appendChild(intro);
    function gone() {
      intro.classList.add('is-gone');
      setTimeout(function () { if (intro.parentNode) intro.parentNode.removeChild(intro); }, 800);
    }
    if (doc.readyState === 'complete') setTimeout(gone, 350);
    else window.addEventListener('load', function () { setTimeout(gone, 350); });
    // hard safety: never trap the page
    setTimeout(gone, 2600);
  }

  /* ========================= SCROLL PROGRESS ========================= */
  function initProgress() {
    if (reduceMotion) return;
    var bar = doc.createElement('div');
    bar.className = 'meg-progress';
    doc.body.appendChild(bar);
    var ticking = false;
    function upd() {
      ticking = false;
      var h = doc.documentElement.scrollHeight - window.innerHeight;
      bar.style.width = (h > 0 ? (window.pageYOffset / h) * 100 : 0) + '%';
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; requestAnimationFrame(upd); }
    }, { passive: true });
    upd();
  }

  /* ========================= CUSTOM CURSOR ========================== */
  function initCursor() {
    if (!allowPointerFx) return;
    var dot = doc.createElement('div'); dot.className = 'meg-cursor';
    var ring = doc.createElement('div'); ring.className = 'meg-cursor-ring';
    doc.body.appendChild(dot); doc.body.appendChild(ring);
    doc.body.classList.add('meg-has-cursor');
    var x = window.innerWidth / 2, y = window.innerHeight / 2, rx = x, ry = y;
    window.addEventListener('mousemove', function (e) {
      x = e.clientX; y = e.clientY;
      dot.style.left = x + 'px'; dot.style.top = y + 'px';
      dot.classList.add('is-active'); ring.classList.add('is-active');
    }, { passive: true });
    window.addEventListener('mouseout', function () {
      dot.classList.remove('is-active'); ring.classList.remove('is-active');
    });
    var hoverSel = 'a, button, .meg-card, .meg-cat-card, input, [role="button"]';
    doc.addEventListener('mouseover', function (e) {
      if (e.target.closest && e.target.closest(hoverSel)) ring.classList.add('is-hover');
    });
    doc.addEventListener('mouseout', function (e) {
      if (e.target.closest && e.target.closest(hoverSel)) ring.classList.remove('is-hover');
    });
    (function loop() {
      rx = lerp(rx, x, 0.18); ry = lerp(ry, y, 0.18);
      ring.style.left = rx + 'px'; ring.style.top = ry + 'px';
      requestAnimationFrame(loop);
    })();
  }

  /* ====================== SCROLL REVEALS ============================ */
  function initReveals() {
    var groups = [
      ['.meg-trust__item', 'meg-reveal--soft', false],
      ['.meg-section__header', 'meg-reveal', false],
      ['.meg-card', 'meg-reveal--scale', true],
      ['.meg-cat-card', 'meg-reveal', true],
      ['.meg-origin__content', 'meg-reveal', false],
      ['.meg-origin__visual', 'meg-reveal--scale', false],
      ['.meg-banner__img-wrap', 'meg-reveal--scale', false],
      ['.meg-insta__grid > *', 'meg-reveal--soft', true],
      ['.rte > p, .rte > h2, .rte > h3, .rte > ul, .rte > blockquote', 'meg-reveal--soft', true],
      /* sub-pages (Our Story / Origin / Shipping / Contact) */
      ['.meg-split__content', 'meg-reveal', false],
      ['.meg-split__img', 'meg-reveal--scale', false],
      ['.meg-val', 'meg-reveal--soft', true],
      ['.meg-cs__h, .meg-cs__p', 'meg-reveal--soft', true],
      ['.meg-cta__title', 'meg-reveal', false],
      ['.meg-page-hero__sub', 'meg-reveal--soft', false],
      ['.meg-divider', 'meg-reveal', false],
      /* shop + product pages */
      ['.meg-shop-hero__sub', 'meg-reveal--soft', false],
      ['.mp-gallery__main', 'meg-reveal--scale', false],
      ['.mp-related__card', 'meg-reveal--scale', true],
      ['.mp-related__title', 'meg-reveal', false]
    ];
    var all = [];
    groups.forEach(function (g) {
      doc.querySelectorAll(g[0]).forEach(function (el, i) {
        if (el.dataset.megReveal) return;
        el.dataset.megReveal = '1';
        el.classList.add('meg-reveal');
        if (g[1] !== 'meg-reveal') el.classList.add(g[1]);
        if (g[2]) el.style.transitionDelay = ((i % 8) * 0.07) + 's';
        all.push(el);
      });
    });
    if (!('IntersectionObserver' in window) || reduceMotion) {
      all.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (ents) {
      ents.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
      });
    }, { rootMargin: '0px 0px -7% 0px', threshold: 0.1 });
    all.forEach(function (el) { io.observe(el); });
    // Safety net: nothing may stay invisible. Force-reveal any stragglers.
    setTimeout(function () {
      all.forEach(function (el) {
        if (!el.classList.contains('is-in')) {
          var r = el.getBoundingClientRect();
          if (r.top < window.innerHeight * 1.2) el.classList.add('is-in');
        }
      });
    }, 2600);
  }

  /* ====================== HEADLINE WORD RISE ======================== */
  function wrapWords(el) {
    if (!el || el.dataset.megWords) return;
    el.dataset.megWords = '1';
    var nodes = Array.prototype.slice.call(el.childNodes);
    nodes.forEach(function (node) {
      if (node.nodeType === 3 && node.textContent.trim()) {
        var frag = doc.createDocumentFragment();
        node.textContent.split(/(\s+)/).forEach(function (tok) {
          if (!tok.trim()) { frag.appendChild(doc.createTextNode(tok)); return; }
          var w = doc.createElement('span'); w.className = 'meg-word';
          var inner = doc.createElement('span'); inner.textContent = tok;
          w.appendChild(inner); frag.appendChild(w);
        });
        el.replaceChild(frag, node);
      }
    });
  }
  function initWordRise() {
    if (reduceMotion) return;
    var targets = [].concat(
      Array.prototype.slice.call(doc.querySelectorAll('.meg-hero__content h1')),
      Array.prototype.slice.call(doc.querySelectorAll('.meg-section__title, .meg-origin__title, .meg-page-hero__title, .meg-shop-hero__title, .mp-title'))
    );
    targets.forEach(function (el) { wrapWords(el); });
    if (!('IntersectionObserver' in window)) {
      targets.forEach(function (el) {
        el.querySelectorAll('.meg-word').forEach(function (w) { w.classList.add('is-in'); });
      });
      return;
    }
    var io = new IntersectionObserver(function (ents) {
      ents.forEach(function (e) {
        if (!e.isIntersecting) return;
        var ws = e.target.querySelectorAll('.meg-word');
        ws.forEach(function (w, i) { w.style.transitionDelay = (i * 0.05) + 's'; w.classList.add('is-in'); });
        io.unobserve(e.target);
      });
    }, { threshold: 0.2 });
    targets.forEach(function (el) { io.observe(el); });
    // hero is above the fold — trigger immediately
    var hero = doc.querySelector('.meg-hero__content h1');
    if (hero) requestAnimationFrame(function () {
      hero.querySelectorAll('.meg-word').forEach(function (w, i) {
        w.style.transitionDelay = (0.25 + i * 0.06) + 's'; w.classList.add('is-in');
      });
    });
  }

  /* ====================== SHIMMER + TILT + AURORA ================== */
  function initShimmer() {
    doc.querySelectorAll('.meg-card__image').forEach(function (img) {
      if (img.querySelector('.meg-shimmer')) return;
      var s = doc.createElement('span'); s.className = 'meg-shimmer'; img.appendChild(s);
    });
  }

  /* ============ LIVE PRICES (always reflect current product price) ============ */
  function initLivePrices() {
    if (!window.fetch) return;
    var cards = doc.querySelectorAll('.meg-card, .mp-related__card, .prod');
    if (!cards.length) return;
    var cache = {};
    function fmtIDR(idr) {
      try { return 'Rp ' + new Intl.NumberFormat('id-ID').format(idr); }
      catch (e) { return 'Rp ' + String(idr).replace(/\B(?=(\d{3})+(?!\d))/g, '.'); }
    }
    function getProduct(handle) {
      if (!cache[handle]) {
        cache[handle] = fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.ok ? r.json() : null; })
          .catch(function () { return null; });
      }
      return cache[handle];
    }
    cards.forEach(function (card) {
      var link = card.matches('a[href*="/products/"]') ? card : card.querySelector('a[href*="/products/"]');
      var priceEl = card.querySelector('.meg-card__price, .mp-related__card-price, .prod__price');
      if (!link || !priceEl) return;
      var m = link.getAttribute('href').match(/\/products\/([^/?#]+)/);
      if (!m) return;
      getProduct(m[1]).then(function (d) {
        if (!d || typeof d.price !== 'number') return;
        priceEl.textContent = fmtIDR(Math.round(d.price / 100));
      });
    });
  }

  function initTilt() {
    if (!allowPointerFx) return;
    var MAX = 7;
    function bind(el, lift) {
      var raf = null, tx = 0, ty = 0;
      el.addEventListener('mousemove', function (ev) {
        var r = el.getBoundingClientRect();
        var px = (ev.clientX - r.left) / r.width - 0.5;
        var py = (ev.clientY - r.top) / r.height - 0.5;
        ty = px * MAX; tx = -py * MAX;
        el.style.setProperty('--mx', ((ev.clientX - r.left) / r.width * 100) + '%');
        el.style.setProperty('--my', ((ev.clientY - r.top) / r.height * 100) + '%');
        if (!raf) raf = requestAnimationFrame(function () {
          raf = null;
          el.style.transform = 'perspective(900px) rotateX(' + tx.toFixed(2) + 'deg) rotateY(' +
            ty.toFixed(2) + 'deg) translateZ(' + lift + 'px)';
        });
      });
      el.addEventListener('mouseleave', function () {
        if (raf) { cancelAnimationFrame(raf); raf = null; } el.style.transform = '';
      });
    }
    doc.querySelectorAll('.meg-card, .mp-related__card, .prod').forEach(function (el) { bind(el, 14); });
    doc.querySelectorAll('.meg-cat-card, .cat').forEach(function (el) { bind(el, 8); });
  }
  function initMagnetic() {
    if (!allowPointerFx) return;
    doc.querySelectorAll('.btn-primary, .btn-outline-dark, .btn--gold, .btn--ghost').forEach(function (b) {
      b.addEventListener('mousemove', function (e) {
        var r = b.getBoundingClientRect();
        var mx = (e.clientX - r.left - r.width / 2) * 0.3;
        var my = (e.clientY - r.top - r.height / 2) * 0.4;
        b.style.transform = 'translate(' + mx.toFixed(1) + 'px,' + my.toFixed(1) + 'px)';
      });
      b.addEventListener('mouseleave', function () { b.style.transform = ''; });
    });
  }
  function initAurora() {
    doc.querySelectorAll('.meg-section--dark, .meg-origin, .meg-hero, .meg-page-hero, .meg-cta, .meg-shop-hero').forEach(function (s) {
      if (s.classList.contains('meg-aurora')) return;
      s.classList.add('meg-aurora');
      var fx = doc.createElement('div'); fx.className = 'meg-aurora__fx';
      s.insertBefore(fx, s.firstChild);
    });
  }

  /* =========================== PARALLAX ============================ */
  function initParallax() {
    if (reduceMotion) return;
    var bg = doc.querySelector('.meg-hero__bg'), hero = doc.querySelector('.meg-hero');
    if (!bg || !hero) return;
    var ticking = false;
    function upd() {
      ticking = false;
      var y = window.pageYOffset;
      if (y > window.innerHeight) return;
      bg.style.transform = 'scale(1.08) translateY(' + (y * 0.2).toFixed(1) + 'px)';
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; requestAnimationFrame(upd); }
    }, { passive: true });
  }

  /* ======================== SMOOTH SCROLL ========================= */
  function initSmoothScroll() {
    if (!hoverable || reduceMotion || lowPower) return;
    loadScript('https://cdn.jsdelivr.net/npm/@studio-freight/lenis@1.0.42/dist/lenis.min.js')
      .then(function () {
        if (!window.Lenis) return;
        var l = new window.Lenis({ duration: 1.15, smoothWheel: true });
        (function raf(t) { l.raf(t); requestAnimationFrame(raf); })();
      }).catch(function () {});
  }

  /* ===================== THE 3D CRYSTAL =========================== */
  function glowTexture(THREE) {
    var c = doc.createElement('canvas'); c.width = c.height = 256;
    var g = c.getContext('2d');
    var grd = g.createRadialGradient(128, 128, 0, 128, 128, 128);
    grd.addColorStop(0, 'rgba(190,130,225,0.9)');
    grd.addColorStop(0.25, 'rgba(150,70,190,0.55)');
    grd.addColorStop(0.6, 'rgba(90,40,140,0.18)');
    grd.addColorStop(1, 'rgba(0,0,0,0)');
    g.fillStyle = grd; g.fillRect(0, 0, 256, 256);
    var t = new THREE.Texture(c); t.needsUpdate = true; return t;
  }
  function crystalGeometry(THREE) {
    var r = 1.0, aT = 2.05, aB = -1.55, sT = 1.05, sB = -0.55, ring = [];
    for (var i = 0; i < 6; i++) { var a = Math.PI / 6 + i * Math.PI / 3; ring.push([Math.cos(a) * r, Math.sin(a) * r]); }
    var pos = [];
    function tri(a, b, c) { pos.push(a[0], a[1], a[2], b[0], b[1], b[2], c[0], c[1], c[2]); }
    var T = [0, aT, 0], B = [0, aB, 0];
    for (var j = 0; j < 6; j++) {
      var k = (j + 1) % 6;
      var u0 = [ring[j][0], sT, ring[j][1]], u1 = [ring[k][0], sT, ring[k][1]];
      var l0 = [ring[j][0], sB, ring[j][1]], l1 = [ring[k][0], sB, ring[k][1]];
      tri(T, u0, u1); tri(u0, l0, l1); tri(u0, l1, u1); tri(B, l1, l0);
    }
    var geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    geo.computeVertexNormals();
    return geo;
  }
  function initCrystal() {
    var canvas = doc.getElementById('meg-hero-canvas');
    var hero = doc.querySelector('.meg-hero');
    if (!canvas || !hero || !allowCrystal) return;

    loadScript('https://cdnjs.cloudflare.com/ajax/libs/three.js/r134/three.min.js')
      .then(function () {
        var THREE = window.THREE; if (!THREE) return;
        var renderer = new THREE.WebGLRenderer({ canvas: canvas, alpha: true, antialias: true });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        function size() { var w = hero.clientWidth, h = hero.clientHeight; renderer.setSize(w, h, false); return { w: w, h: h }; }
        var d = size();
        var scene = new THREE.Scene();
        var camera = new THREE.PerspectiveCamera(36, d.w / d.h, 0.1, 100);
        camera.position.set(0, 0, 7.6);
        var group = new THREE.Group(); scene.add(group);

        var geo = crystalGeometry(THREE);
        var crystal = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({
          color: 0x8233a8, metalness: 0.0, roughness: 0.05, flatShading: true,
          transparent: true, opacity: 0.92, emissive: 0x3a0f5e, emissiveIntensity: 0.6
        }));
        group.add(crystal);
        // glowing inner core
        var core = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({
          color: 0xc488e6, transparent: true, opacity: 0.32, blending: THREE.AdditiveBlending, depthWrite: false
        }));
        core.scale.setScalar(0.55); group.add(core);
        // gold facet edges
        var edges = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({
          color: 0xd4af37, wireframe: true, transparent: true, opacity: 0.16
        }));
        edges.scale.setScalar(1.004); group.add(edges);
        // bloom halo sprite
        var halo = new THREE.Sprite(new THREE.SpriteMaterial({
          map: glowTexture(THREE), color: 0xffffff, transparent: true,
          opacity: 0.9, blending: THREE.AdditiveBlending, depthWrite: false
        }));
        halo.scale.set(6, 6, 1); halo.position.z = -1.5; scene.add(halo);

        scene.add(new THREE.AmbientLight(0x3a1a5a, 0.7));
        var key = new THREE.DirectionalLight(0xd4af37, 1.7); key.position.set(3, 4, 5); scene.add(key);
        var fill = new THREE.DirectionalLight(0xb784c7, 1.0); fill.position.set(-4, -1, 3); scene.add(fill);
        var rim = new THREE.PointLight(0xffffff, 1.3, 30); rim.position.set(-2, 3, -4); scene.add(rim);

        // dust
        var N = 130, parr = new Float32Array(N * 3);
        for (var p = 0; p < N; p++) { parr[p*3]=(Math.random()-0.5)*10; parr[p*3+1]=(Math.random()-0.5)*8; parr[p*3+2]=(Math.random()-0.5)*5; }
        var pg = new THREE.BufferGeometry(); pg.setAttribute('position', new THREE.Float32BufferAttribute(parr, 3));
        var dust = new THREE.Points(pg, new THREE.PointsMaterial({
          color: 0xe2cdf5, size: 0.05, transparent: true, opacity: 0.6,
          blending: THREE.AdditiveBlending, depthWrite: false
        }));
        scene.add(dust);

        var tmx = 0, tmy = 0, mx = 0, my = 0;
        window.addEventListener('mousemove', function (e) {
          tmx = e.clientX / window.innerWidth - 0.5; tmy = e.clientY / window.innerHeight - 0.5;
        }, { passive: true });

        var visible = true;
        if ('IntersectionObserver' in window)
          new IntersectionObserver(function (en) { visible = en[0].isIntersecting; }, { threshold: 0.01 }).observe(hero);
        window.addEventListener('resize', function () {
          var nd = size(); camera.aspect = nd.w / nd.h; camera.updateProjectionMatrix();
        }, { passive: true });

        var painted = false, t0 = performance.now();
        (function frame(now) {
          requestAnimationFrame(frame);
          if (!visible) return;
          var t = (now - t0) / 1000;
          mx = lerp(mx, tmx, 0.05); my = lerp(my, tmy, 0.05);
          group.rotation.y = t * 0.32 + mx * 0.7;
          group.rotation.x = Math.sin(t * 0.4) * 0.12 - my * 0.55;
          group.position.y = Math.sin(t * 0.8) * 0.12;
          core.rotation.copy(group.rotation);
          // moving light sparkle
          key.position.x = Math.cos(t * 0.6) * 4; key.position.z = Math.sin(t * 0.6) * 4 + 2;
          halo.material.opacity = 0.55 + Math.sin(t * 1.3) * 0.15;
          dust.rotation.y = t * 0.04;
          renderer.render(scene, camera);
          if (!painted) { painted = true; canvas.classList.add('is-live'); hero.classList.add('has-crystal'); }
        })(t0);
      }).catch(function () {});
  }

  /* ============================= BOOT ============================== */
  function boot() {
    initPageTransition();
    initIntro();
    initProgress();
    initAurora();
    initReveals();
    initWordRise();
    initShimmer();
    initLivePrices();
    initParallax();
    initCursor();
    initTilt();
    initMagnetic();
    onIdle(function () { initCrystal(); initSmoothScroll(); });
  }
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
